import { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  LayoutDashboard,
  UserCog,
  Settings,
  LogOut,
  Shield,
  Menu,
  X,
  DollarSign,
  Users,
  CreditCard,
  Car,
  UserCircle,
  Bike,
  Sparkles,
  Smartphone,
  Search,
  TrendingUp,
  Key,
  CloudUpload,
  MapPin,
} from "lucide-react";

// Import page components
import { OverviewPage } from "./components/OverviewPage";
import { ManageAdminsPage } from "./components/ManageAdminsPage";
import { OTPConfigPage } from "./components/OTPConfigPage";
import { SubSuperAdminsPage } from "./components/SubSuperAdminsPage";
import { AdminPricingPage } from "./components/AdminPricingPage";
import { AdminPaymentsPage } from "./components/AdminPaymentsPage";
import { AllDriversPage } from "./components/AllDriversPage";
import { AllCustomersPage } from "./components/AllCustomersPage";
import CityDemandPage from "./components/CityDemandPage";
import { VehicleSettingsPage } from "./components/VehicleSettingsPage";
import MoreServicesManagement from "./components/MoreServicesManagement";
import { AppSettingsPage, SEOSettingsPage, OTPHistoryPage } from "./components";
import LiveUpdatesPage from "./components/LiveUpdatesPage";
import OrderDebugPage from "../admin/components/OrderDebugPage";
import { CitiesPage } from "./components/CitiesPage";

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    if (path === "admins") setActiveTab("admins");
    else if (path === "drivers") setActiveTab("drivers");
    else if (path === "customers") setActiveTab("customers");
    else if (path === "cities") setActiveTab("cities");
    else if (path === "city-demand") setActiveTab("city-demand");
    else if (path === "config") setActiveTab("config");
    else if (path === "sub-superadmins") setActiveTab("sub-superadmins");
    else if (path === "pricing") setActiveTab("pricing");
    else if (path === "payments") setActiveTab("payments");
    else if (path === "vehicles") setActiveTab("vehicles");
    else if (path === "more-services") setActiveTab("more-services");
    else if (path === "app-settings") setActiveTab("app-settings");
    else if (path === "seo-settings") setActiveTab("seo-settings");
    else if (path === "otp-history") setActiveTab("otp-history");
    else if (path === "live-updates") setActiveTab("live-updates");
    else if (path === "order-debug") setActiveTab("order-debug");
    else setActiveTab("dashboard");
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const navItems = [
    { id: "dashboard", icon: LayoutDashboard, label: "Overview", path: "/superadmin" },
    { id: "admins", icon: UserCog, label: "Manage Admins", path: "/superadmin/admins" },
    { id: "cities", icon: MapPin, label: "Cities", path: "/superadmin/cities" },
    { id: "drivers", icon: Car, label: "All Drivers", path: "/superadmin/drivers" },
    { id: "customers", icon: UserCircle, label: "All Customers", path: "/superadmin/customers" },
    { id: "city-demand", icon: TrendingUp, label: "City Demand", path: "/superadmin/city-demand" },
    { id: "vehicles", icon: Bike, label: "Vehicle Settings", path: "/superadmin/vehicles" },
    { id: "more-services", icon: Sparkles, label: "More Services", path: "/superadmin/more-services" },
    { id: "app-settings", icon: Smartphone, label: "App Settings", path: "/superadmin/app-settings" },
    { id: "seo-settings", icon: Search, label: "SEO Settings", path: "/superadmin/seo-settings" },
    { id: "live-updates", icon: CloudUpload, label: "Live Updates", path: "/superadmin/live-updates" },
    { id: "order-debug", icon: Search, label: "Order Debug", path: "/superadmin/order-debug" },
    { id: "otp-history", icon: Key, label: "OTP History", path: "/superadmin/otp-history" },
    { id: "payments", icon: CreditCard, label: "Admin Payments", path: "/superadmin/payments" },
    { id: "sub-superadmins", icon: Shield, label: "Sub-Super-Admins", path: "/superadmin/sub-superadmins" },
    { id: "pricing", icon: DollarSign, label: "Admin Pricing", path: "/superadmin/pricing" },
    { id: "config", icon: Settings, label: "Settings", path: "/superadmin/config" },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gradient-to-r from-purple-900 to-indigo-900 text-white">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-white hover:bg-white/10"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="font-bold text-white">Super Admin</h1>
              <p className="text-xs text-white/70">{user?.name}</p>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Button
                key={item.id}
                variant={activeTab === item.id ? "default" : "ghost"}
                size="sm"
                onClick={() => navigate(item.path)}
                className={activeTab === item.id 
                  ? "bg-white text-purple-700 hover:bg-gray-100" 
                  : "text-white hover:text-white hover:bg-white/10"}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-4 h-4 mr-2" />
                {item.label}
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white hover:text-white hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </nav>

          {/* Mobile User Info & Logout */}
          <div className="md:hidden flex items-center gap-2">
            <span className="text-sm font-medium">{user?.name?.split(' ')[0]}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-white hover:bg-white/10"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-40 transform transition-transform duration-300 ease-in-out md:hidden flex flex-col ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="p-4 border-b border-border bg-gradient-to-r from-purple-900 to-indigo-900 flex-shrink-0">
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold">Super Admin</h2>
              <p className="text-xs text-white/70">{user?.email}</p>
            </div>
          </div>
        </div>
        
        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigation(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-muted"
              }`}
              data-testid={`mobile-nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="p-4 md:p-6">
        <Routes>
          <Route index element={<OverviewPage />} />
          <Route path="admins" element={<ManageAdminsPage />} />
          <Route path="cities" element={<CitiesPage />} />
          <Route path="drivers" element={<AllDriversPage />} />
          <Route path="customers" element={<AllCustomersPage />} />
          <Route path="city-demand" element={<CityDemandPage />} />
          <Route path="vehicles" element={<VehicleSettingsPage />} />
          <Route path="more-services" element={<MoreServicesManagement />} />
          <Route path="app-settings" element={<AppSettingsPage user={user} />} />
          <Route path="seo-settings" element={<SEOSettingsPage user={user} />} />
          <Route path="live-updates" element={<LiveUpdatesPage />} />
          <Route path="order-debug" element={<OrderDebugPage />} />
          <Route path="otp-history" element={<OTPHistoryPage />} />
          <Route path="payments" element={<AdminPaymentsPage />} />
          <Route path="sub-superadmins" element={<SubSuperAdminsPage />} />
          <Route path="pricing" element={<AdminPricingPage />} />
          <Route path="config" element={<OTPConfigPage />} />
        </Routes>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
